import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import "./ClassesTests.css";
import "./dashboard.css";
import Sidebar from "./Sidebar";
import {
  FaPaperPlane,
  FaPrint,
  FaSearch,
  FaFilter,
  FaSave,
  FaTimes,
  FaCalendarAlt,
  FaBook,
  FaClipboardCheck,
  FaArrowUp,
  FaArrowDown,
  FaSpinner,
  FaExclamationTriangle,
  FaDatabase,
  FaCheckCircle,
  FaExclamationCircle,
  FaIdCard,
  FaSignature,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

// PHP API Base URL
const PHP_API_BASE = "https://mhsdev.online/MHS-WEB/api/ManageTestSheets.php";

const TestSheet = () => {
  const location = useLocation();
  const className = location.state?.className || "Default Class";
  const navigate = useNavigate();
  const inputRefs = useRef([]);

  const [formData, setFormData] = useState({
    testNo: "",
    testName: "", // New field
    testId: "", // Will be system-generated
    subject: "",
    totalMarks: "",
    classSection: "",
    date: new Date().toISOString().split("T")[0],
    testDay: new Date().toLocaleString("en-us", { weekday: "long" }),
  });

  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [sections, setSections] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [currentFocusIndex, setCurrentFocusIndex] = useState(-1);
  const [verificationResult, setVerificationResult] = useState(null);
  const [generatedTestId, setGeneratedTestId] = useState("");
  const [showFiltersPanel, setShowFiltersPanel] = useState(false); // UPDATED: Default hidden
  const [apiStatus, setApiStatus] = useState({
    subjects: false,
    sections: false,
    students: false,
    submit: false,
  });

  // Function to handle API errors
  const handleApiError = (error, context = "operation") => {
    console.error(`API Error in ${context}:`, error);

    let errorMessage = error.message || "Unknown error occurred";
    if (error.response) {
      errorMessage =
        error.response.data?.error ||
        error.response.data?.message ||
        `HTTP ${error.response.status}`;
    }

    Swal.fire({
      icon: "error",
      title: "Server Error",
      text: `Failed to ${context}. ${errorMessage}`,
      confirmButtonColor: "#e53e3e",
      footer: `<small>Please check if the PHP server is running at ${PHP_API_BASE}</small>`,
    });
  };

  // Generate a test ID
  const generateTestId = async () => {
    try {
      const response = await axios.get(PHP_API_BASE, {
        params: { action: "generate-test-id" },
      });

      if (response.data.success) {
        setGeneratedTestId(response.data.testId);
        setFormData((prev) => ({ ...prev, testId: response.data.testId }));

        Swal.fire({
          title: "Test ID Generated",
          text: `System-generated Test ID: ${response.data.testId}`,
          icon: "success",
          confirmButtonText: "OK",
          confirmButtonColor: "#48bb78",
          timer: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to generate test ID:", error);
      // Fallback to local generation
      const fallbackId = Math.floor(
        10000000 + Math.random() * 90000000
      ).toString();
      setGeneratedTestId(fallbackId);
      setFormData((prev) => ({ ...prev, testId: fallbackId }));
    }
  };

  // Verify test details for duplicates
  const verifyTestDetails = async () => {
    // Validate required fields
    const requiredFields = ["testNo", "testName", "subject", "classSection"];
    const missingFields = requiredFields.filter((field) => !formData[field]);

    if (missingFields.length > 0) {
      Swal.fire({
        title: "Missing Information",
        text: `Please fill in: ${missingFields.join(", ")}`,
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    setIsVerifying(true);
    setVerificationResult(null);

    try {
      const payload = {
        testNo: formData.testNo,
        testName: formData.testName,
        subject: formData.subject,
        class: className,
        section: formData.classSection,
        date: formData.date,
      };

      const response = await axios.post(
        `${PHP_API_BASE}?action=verify-test-details`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      setVerificationResult(response.data);

      if (response.data.isDuplicate) {
        // Show detailed duplicate information
        Swal.fire({
          title: "Duplicate Test Detected",
          html: `
            <div style="text-align: left;">
              <div style="background: #fff5f5; padding: 15px; border-radius: 8px; border-left: 4px solid #e53e3e; margin-bottom: 15px;">
                <p style="color: #e53e3e; font-weight: 600; margin-bottom: 10px;">
                  ⚠️ ${response.data.message}
                </p>
                <p><strong>Existing Test Details:</strong></p>
                <ul style="margin-left: 20px;">
                  <li>Test ID: ${response.data.existingTest.test_id}</li>
                  <li>Test Name: ${response.data.existingTest.test_name}</li>
                  <li>Subject: ${response.data.existingTest.subject}</li>
                  <li>Date: ${response.data.existingTest.test_date}</li>
                </ul>
              </div>
              
              ${
                response.data.suggestions &&
                response.data.suggestions.length > 0
                  ? `
                <div style="background: #f7fafc; padding: 15px; border-radius: 8px; margin-bottom: 15px;">
                  <p style="font-weight: 600; margin-bottom: 10px;">📌 Suggestions:</p>
                  <ul style="margin-left: 20px;">
                    ${response.data.suggestions
                      .map((suggestion) => `<li>${suggestion}</li>`)
                      .join("")}
                  </ul>
                </div>
              `
                  : ""
              }
              
              <div style="background: #e6fffa; padding: 15px; border-radius: 8px;">
                <p style="font-weight: 600; margin-bottom: 10px;">💡 Recommendations:</p>
                <ul style="margin-left: 20px;">
                  ${
                    response.data.recommendations
                      ? response.data.recommendations
                          .map((rec) => `<li>${rec}</li>`)
                          .join("")
                      : ""
                  }
                </ul>
              </div>
            </div>
          `,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Change Details",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#3182ce",
          cancelButtonColor: "#a0aec0",
          width: "600px",
        }).then((result) => {
          if (result.isConfirmed) {
            // Focus on test name field for editing
            document.querySelector('input[name="testName"]')?.focus();
          }
        });
      } else {
        // Test details are unique
        Swal.fire({
          title: "✅ Test Details Verified",
          html: `
            <div style="text-align: left;">
              <div style="background: #f0fff4; padding: 15px; border-radius: 8px; border-left: 4px solid #48bb78; margin-bottom: 15px;">
                <p style="color: #48bb78; font-weight: 600;">
                  ✓ Test details are unique and ready for submission.
                </p>
                <p><strong>Generated Test ID:</strong> ${
                  response.data.generatedTestId
                }</p>
              </div>
              
              ${
                response.data.suggestions &&
                response.data.suggestions.length > 0
                  ? `
                <div style="background: #f7fafc; padding: 12px; border-radius: 6px;">
                  <p style="font-weight: 600; margin-bottom: 8px;">ℹ️ Information:</p>
                  <ul style="margin-left: 20px; font-size: 0.9em;">
                    ${response.data.suggestions
                      .map((suggestion) => `<li>${suggestion}</li>`)
                      .join("")}
                  </ul>
                </div>
              `
                  : ""
              }
            </div>
          `,
          icon: "success",
          confirmButtonText: "Continue",
          confirmButtonColor: "#48bb78",
        });

        // Set the generated test ID
        setGeneratedTestId(response.data.generatedTestId);
        setFormData((prev) => ({
          ...prev,
          testId: response.data.generatedTestId,
        }));
        
        // UPDATED: Auto-disappear filters/details when test is successfully verified
        setShowFiltersPanel(false);
      }
    } catch (error) {
      console.error("Verification error:", error);
      Swal.fire({
        title: "Verification Failed",
        text: error.response?.data?.error || "Failed to verify test details",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  // Fetch subjects by class from PHP backend
  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        setApiStatus((prev) => ({ ...prev, subjects: "loading" }));
        const response = await axios.get(PHP_API_BASE, {
          params: {
            action: "get-subjects",
            className: className,
          },
        });

        if (response.data.success) {
          setSubjects(response.data.data || []);
          setApiStatus((prev) => ({ ...prev, subjects: "success" }));
        } else {
          console.warn("No subjects found for class:", className);
          setSubjects([]);
          setApiStatus((prev) => ({ ...prev, subjects: "empty" }));
        }
      } catch (error) {
        handleApiError(error, "fetch subjects");
        setSubjects([]);
        setApiStatus((prev) => ({ ...prev, subjects: "error" }));
      }
    };

    fetchSubjects();
  }, [className]);

  // Fetch sections by class from PHP backend
  useEffect(() => {
    const fetchSections = async () => {
      try {
        setApiStatus((prev) => ({ ...prev, sections: "loading" }));
        const response = await axios.get(PHP_API_BASE, {
          params: {
            action: "get-class-sections",
            className: className,
          },
        });

        if (response.data.success) {
          const sectionsFromDB = response.data.data || [];
          const studentSections = response.data.student_sections || [];

          const allSections = [
            ...new Set([
              ...sectionsFromDB.map((s) => s.section_hint || s.section_name),
              ...studentSections,
            ]),
          ].filter(Boolean);

          setSections(
            allSections.map((section) => ({
              id: section,
              section_hint: section,
              section_name: section,
            }))
          );
          setApiStatus((prev) => ({ ...prev, sections: "success" }));
        } else {
          console.warn("No sections found for class:", className);
          setSections([]);
          setApiStatus((prev) => ({ ...prev, sections: "empty" }));
        }
      } catch (error) {
        handleApiError(error, "fetch sections");
        setSections([]);
        setApiStatus((prev) => ({ ...prev, sections: "error" }));
      }
    };

    fetchSections();
  }, [className]);

  // Fetch students when classSection changes
  useEffect(() => {
    const fetchStudents = async () => {
      if (!formData.classSection) {
        setStudents([]);
        return;
      }

      try {
        setIsLoading(true);
        setApiStatus((prev) => ({ ...prev, students: "loading" }));

        const response = await axios.get(PHP_API_BASE, {
          params: {
            action: "get-students-for-test",
            className: className,
            section_name: formData.classSection,
          },
        });

        if (response.data.success) {
          const studentsData = response.data.data.map((student) => ({
            id: student.student_id,
            name: student.name,
            father_name: student.father_name,
            class: student.class,
            section: student.section,
            subject: formData.subject,
            totalMarks: parseFloat(formData.totalMarks) || 0,
            obtainedMarks: 0,
            percentage: 0,
            date: formData.date,
            day: formData.testDay,
            status: "pending",
            mobile: student.mobile,
            b_form_no: student.b_form_no,
            address: student.address,
            gender: student.gender,
            family_status: student.family_status,
          }));

          setStudents(studentsData);
          setApiStatus((prev) => ({ ...prev, students: "success" }));

          setTimeout(() => {
            if (inputRefs.current[0]) {
              inputRefs.current[0].focus();
              inputRefs.current[0].select();
            }
          }, 100);
        } else {
          console.warn("No students found for section:", formData.classSection);
          setStudents([]);
          setApiStatus((prev) => ({ ...prev, students: "empty" }));
        }
      } catch (error) {
        handleApiError(error, "fetch students");
        setStudents([]);
        setApiStatus((prev) => ({ ...prev, students: "error" }));
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      if (formData.classSection && formData.subject && formData.totalMarks) {
        fetchStudents();
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [
    className,
    formData.classSection,
    formData.subject,
    formData.totalMarks,
    formData.date,
    formData.testDay,
  ]);

  // Keyboard navigation effect
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (
        ["ArrowUp", "ArrowDown", "Enter"].includes(e.key) &&
        document.activeElement.className === "testsheet-marks-input"
      ) {
        e.preventDefault();
        const currentIndex = Array.from(
          document.querySelectorAll(".testsheet-marks-input")
        ).indexOf(document.activeElement);

        if (
          e.key === "ArrowDown" &&
          currentIndex < inputRefs.current.length - 1
        ) {
          const nextInput = inputRefs.current[currentIndex + 1];
          if (nextInput) {
            nextInput.focus();
            nextInput.select();
            setCurrentFocusIndex(currentIndex + 1);
          }
        } else if (e.key === "ArrowUp" && currentIndex > 0) {
          const prevInput = inputRefs.current[currentIndex - 1];
          if (prevInput) {
            prevInput.focus();
            prevInput.select();
            setCurrentFocusIndex(currentIndex - 1);
          }
        } else if (e.key === "Enter") {
          if (currentIndex < inputRefs.current.length - 1) {
            const nextInput = inputRefs.current[currentIndex + 1];
            if (nextInput) {
              nextInput.focus();
              nextInput.select();
              setCurrentFocusIndex(currentIndex + 1);
            }
          } else {
            inputRefs.current[0]?.focus();
            inputRefs.current[0]?.select();
            setCurrentFocusIndex(0);
          }
        }
      }
    };

    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleMarksUpdate = (e, studentId, index) => {
    const value = parseFloat(e.target.value) || 0;
    const totalMarks = parseFloat(formData.totalMarks) || 0;

    if (value > totalMarks) {
      Swal.fire({
        title: "Invalid Input",
        text: `Obtained marks cannot exceed ${totalMarks}.`,
        icon: "warning",
        confirmButtonText: "OK",
        customClass: { popup: "swal-popup" },
        showConfirmButton: true,
        timer: 1500,
      });

      if (inputRefs.current[index]) {
        inputRefs.current[index].focus();
        inputRefs.current[index].select();
      }
      return;
    }

    const updatedStudents = students.map((student) =>
      student.id === studentId
        ? {
            ...student,
            obtainedMarks: value,
            percentage:
              totalMarks > 0 ? ((value / totalMarks) * 100).toFixed(2) : 0,
            status: value > 0 ? "submitted" : "pending",
          }
        : student
    );
    setStudents(updatedStudents);
  };

  const handleBulkMarksUpdate = (marks) => {
    const totalMarks = parseFloat(formData.totalMarks) || 0;
    const updatedStudents = students.map((student) => ({
      ...student,
      obtainedMarks: marks,
      percentage: totalMarks > 0 ? ((marks / totalMarks) * 100).toFixed(2) : 0,
      status: marks > 0 ? "submitted" : "pending",
    }));
    setStudents(updatedStudents);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const requiredFields = [
      "testNo",
      "testName",
      "testId",
      "subject",
      "totalMarks",
      "classSection",
    ];
    const missingFields = requiredFields.filter((field) => !formData[field]);

    if (missingFields.length > 0) {
      Swal.fire({
        title: "Missing Information",
        html: `
          <div style="text-align: left;">
            <p>Please fill in the following fields:</p>
            <ul style="margin-left: 20px;">
              ${missingFields
                .map(
                  (field) =>
                    `<li>${field === "testName" ? "Test Name" : field}</li>`
                )
                .join("")}
            </ul>
          </div>
        `,
        icon: "error",
        confirmButtonText: "OK",
        customClass: { popup: "swal-popup" },
      });
      return;
    }

    // Verify test details before proceeding
    await verifyTestDetails();
  };

  const handleTableSubmit = async () => {
    if (students.length === 0) {
      Swal.fire({
        title: "No Data",
        text: "No student data to submit!",
        icon: "warning",
        confirmButtonText: "OK",
        customClass: { popup: "swal-popup" },
      });
      return;
    }

    const incompleteStudents = students.filter((s) => s.obtainedMarks === 0);
    if (incompleteStudents.length > 0) {
      const result = await Swal.fire({
        title: "Incomplete Data",
        html: `${incompleteStudents.length} student(s) have 0 marks. Continue anyway?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Submit Anyway",
        cancelButtonText: "Review",
        confirmButtonColor: "#00b09b",
        cancelButtonColor: "#6c757d",
        customClass: { popup: "swal-popup" },
      });
      if (!result.isConfirmed) return;
    }

    Swal.fire({
      title: "Final Submission",
      html: `
        <div style="text-align: left;">
          <p>This will save all test marks to the database.</p>
          <p><strong>Test Details:</strong></p>
          <ul style="margin-left: 20px;">
            <li>Test ID: <strong>${formData.testId}</strong> (System Generated)</li>
            <li>Test Name: ${formData.testName}</li>
            <li>Test Number: ${formData.testNo}</li>
            <li>Subject: ${formData.subject}</li>
            <li>Class: ${className}</li>
            <li>Section: ${formData.classSection}</li>
            <li>Date: ${formData.date}</li>
            <li>Total Students: ${students.length}</li>
          </ul>
          <p>Continue?</p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Submit",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#6a11cb",
      cancelButtonColor: "#6c757d",
      customClass: { popup: "swal-popup" },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setIsSubmitting(true);
          setApiStatus((prev) => ({ ...prev, submit: "loading" }));

          const payload = {
            testNo: formData.testNo,
            testId: formData.testId,
            testName: formData.testName,
            subject: formData.subject,
            totalMarks: parseFloat(formData.totalMarks),
            class: className,
            section: formData.classSection,
            date: formData.date,
            testDay: formData.testDay,
            testResults: students.map((student) => ({
              studentId: student.id,
              studentName: student.name,
              obtainedMarks: parseFloat(student.obtainedMarks),
              percentage: parseFloat(student.percentage),
            })),
          };

          console.log("Submitting to PHP backend:", payload);

          const response = await fetch(
            `${PHP_API_BASE}?action=submit-testsheet`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(payload),
            }
          );

          const resultData = await response.json();
          console.log("PHP Response:", resultData);

          if (resultData.success) {
            Swal.fire({
              title: "Success!",
              html: `
                <div style="text-align: left;">
                  <div style="background: #f0fff4; padding: 15px; border-radius: 8px; border-left: 4px solid #48bb78; margin-bottom: 15px;">
                    <p style="color: #48bb78; font-weight: 600;">
                      ✓ ${resultData.message}
                    </p>
                  </div>
                  <div style="background: #f7fafc; padding: 12px; border-radius: 6px;">
                    <p style="margin: 0 0 8px 0; font-weight: 600;">Submission Summary:</p>
                    <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;">
                      <div style="color: #38a169;">
                        <span style="font-weight: 600;">✓ Students:</span> ${
                          resultData.stats?.successful || 0
                        }
                      </div>
                      <div style="color: #4a5568;">
                        <span style="font-weight: 600;">🆔 Test ID:</span> ${
                          formData.testId
                        }
                      </div>
                      <div style="color: #4a5568;">
                        <span style="font-weight: 600;">📅 Date:</span> ${
                          formData.date
                        }
                      </div>
                      <div style="color: #4a5568;">
                        <span style="font-weight: 600;">📚 Subject:</span> ${
                          formData.subject
                        }
                      </div>
                      <div style="color: #4a5568;">
                        <span style="font-weight: 600;">🏫 Class:</span> ${className}
                      </div>
                      <div style="color: #4a5568;">
                        <span style="font-weight: 600;">👥 Section:</span> ${
                          formData.classSection
                        }
                      </div>
                    </div>
                  </div>
                </div>
              `,
              icon: "success",
              confirmButtonText: "OK",
              confirmButtonColor: "#00b09b",
              customClass: { popup: "swal-popup" },
            });
            setApiStatus((prev) => ({ ...prev, submit: "success" }));

            // Reset form after successful submission
            setTimeout(() => {
              setIsModalOpen(true);
              setStudents([]);
              setFormData({
                testNo: "",
                testName: "",
                testId: "",
                subject: "",
                totalMarks: "",
                classSection: "",
                date: new Date().toISOString().split("T")[0],
                testDay: new Date().toLocaleString("en-us", {
                  weekday: "long",
                }),
              });
              setGeneratedTestId("");
              setVerificationResult(null);
            }, 2000);
          } else {
            throw new Error(resultData.error || "Submission failed");
          }
        } catch (error) {
          console.error("Error submitting data:", error);

          let errorMessage = error.message;
          if (error.response?.data?.error) {
            errorMessage = error.response.data.error;
          } else if (error.message?.includes("Duplicate")) {
            errorMessage =
              "Duplicate entry detected. This test may already exist for the selected date, section, and subject.";
          }

          Swal.fire({
            title: "Submission Failed",
            html: `
              <div style="text-align: left;">
                <p style="color: #e53e3e; font-weight: 600;">${errorMessage}</p>
                <div style="background: #fff5f5; padding: 12px; border-radius: 6px; margin-top: 1rem; border-left: 4px solid #e53e3e;">
                  <p style="margin: 0 0 8px 0; font-weight: 600; color: #718096;">Troubleshooting Steps:</p>
                  <ul style="margin-left: 20px; color: #718096;">
                    <li>Check if PHP backend is running</li>
                    <li>Verify database connection is working</li>
                    <li>Ensure test details are unique</li>
                    <li>Try verifying test details again</li>
                  </ul>
                </div>
              </div>
            `,
            icon: "error",
            confirmButtonText: "OK",
            customClass: { popup: "swal-popup" },
          });
          setApiStatus((prev) => ({ ...prev, submit: "error" }));
        } finally {
          setIsSubmitting(false);
        }
      }
    });
  };

  const handleSendData = () => {
    navigate("/Print-Test-Sheet-Sample", {
      state: { students, formData, className },
    });
  };

  const filteredStudents = students.filter(
    (student) =>
      student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.id.toString().includes(searchTerm)
  );

  const calculateStatistics = () => {
    const totalStudents = students.length;
    const submittedStudents = students.filter(
      (s) => s.obtainedMarks > 0
    ).length;
    const averageMarks =
      students.reduce((sum, s) => sum + parseFloat(s.obtainedMarks), 0) /
        totalStudents || 0;
    const highestMarks = Math.max(...students.map((s) => s.obtainedMarks));

    return {
      totalStudents,
      submittedStudents,
      averageMarks: averageMarks.toFixed(2),
      highestMarks,
      completionRate:
        totalStudents > 0
          ? ((submittedStudents / totalStudents) * 100).toFixed(1)
          : 0,
    };
  };

  const stats = calculateStatistics();

  const getStatusColor = (status) => {
    switch (status) {
      case "success":
        return "#48bb78";
      case "loading":
        return "#4299e1";
      case "error":
        return "#e53e3e";
      case "empty":
        return "#d69e2e";
      default:
        return "#a0aec0";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "success":
        return "✓ Connected";
      case "loading":
        return "⏳ Loading...";
      case "error":
        return "✗ Error";
      case "empty":
        return "⚠ No Data";
      default:
        return "--";
    }
  };

  return (
    <div className="dashboard-wrapper">
      <Sidebar />

      <main className="main-content testsheet-main-container">
        {/* Modal Overlay */}
        {isModalOpen && (
          <div className="testsheet-modal-overlay">
            <div className="testsheet-modal-card testsheet-compact-modal">
              <div className="testsheet-modal-header" style={{background:'#6a11cb'}}>
                <div className="testsheet-modal-icon">
                  <FaClipboardCheck />
                </div>
                <div className="testsheet-modal-title-wrapper" >
                  <h2 className="testsheet-modal-title">New Test Entry</h2>
                  <p className="testsheet-modal-subtitle">Class {className}</p>
                </div>
                <button
                  className="testsheet-modal-close-btn"
                  onClick={() => setIsModalOpen(false)}
                >
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="testsheet-modal-form">
                <div className="testsheet-form-grid testsheet-compact-grid">
                  {/* Test Name */}
                  <div className="testsheet-form-group">
                    <label className="testsheet-form-label">
                      <span className="testsheet-label-icon">
                        <FaSignature />
                      </span>
                      Test Name *
                    </label>
                    <input
                      type="text"
                      name="testName"
                      value={formData.testName}
                      onChange={handleInputChange}
                      className="testsheet-form-input"
                      placeholder="e.g., Mid-Term, Unit Test, Quiz"
                      autoFocus
                    />
                  </div>

                  {/* Test Number */}
                  <div className="testsheet-form-group">
                    <label className="testsheet-form-label">
                      <span className="testsheet-label-icon">#</span>
                      Test Number *
                    </label>
                    <input
                      type="text"
                      name="testNo"
                      value={formData.testNo}
                      onChange={handleInputChange}
                      className="testsheet-form-input"
                      placeholder="e.g., 1, 2, A, B"
                    />
                  </div>

                  {/* System Generated Test ID - FULL WIDTH */}
                  <div className="testsheet-form-group testsheet-full-width">
                    <label className="testsheet-form-label">
                      <span className="testsheet-label-icon">
                        <FaIdCard />
                      </span>
                      Test ID (System Generated)
                    </label>
                    <div className="testsheet-test-id-container">
                      <input
                        type="text"
                        name="testId"
                        value={formData.testId || generatedTestId}
                        readOnly
                        className="testsheet-form-input testsheet-test-id-input"
                        placeholder="Click Generate or Verify to get ID"
                        style={{ backgroundColor: "#f7fafc" }}
                      />
                      <button
                        type="button"
                        className="testsheet-btn testsheet-btn-outline testsheet-btn-sm"
                        onClick={generateTestId}
                        style={{ whiteSpace: "nowrap" }}
                      >
                        Generate ID
                      </button>
                    </div>
                    {formData.testId && (
                      <small
                        className="testsheet-text-success"
                        style={{ display: "block", marginTop: "4px" }}
                      >
                        ✓ 8-digit unique ID generated
                      </small>
                    )}
                  </div>

                  {/* Subject */}
                  <div className="testsheet-form-group">
                    <label className="testsheet-form-label">
                      <span className="testsheet-label-icon">
                        <FaBook />
                      </span>
                      Subject *
                      {apiStatus.subjects && (
                        <span
                          className="testsheet-status-badge"
                          style={{
                            backgroundColor: getStatusColor(apiStatus.subjects),
                            marginLeft: "8px",
                            fontSize: "0.7em",
                            padding: "2px 6px",
                          }}
                        >
                          {getStatusText(apiStatus.subjects)}
                        </span>
                      )}
                    </label>
                    <div className="testsheet-select-wrapper">
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="testsheet-form-select"
                        disabled={
                          apiStatus.subjects === "loading" ||
                          apiStatus.subjects === "error"
                        }
                      >
                        <option value="">Select Subject *</option>
                        {subjects.length === 0 ? (
                          <option value="" disabled>
                            {apiStatus.subjects === "loading"
                              ? "Loading subjects..."
                              : apiStatus.subjects === "error"
                              ? "Error loading subjects"
                              : "No subjects found"}
                          </option>
                        ) : (
                          subjects.map((subject) => (
                            <option key={subject.id} value={subject.name}>
                              {subject.name}{" "}
                              {subject.code ? `(${subject.code})` : ""}
                            </option>
                          ))
                        )}
                      </select>
                      <div className="testsheet-select-arrow">▼</div>
                    </div>
                  </div>

                  {/* Total Marks */}
                  <div className="testsheet-form-group">
                    <label className="testsheet-form-label">
                      <span className="testsheet-label-icon">📊</span>
                      Total Marks *
                    </label>
                    <input
                      type="number"
                      name="totalMarks"
                      value={formData.totalMarks}
                      onChange={handleInputChange}
                      className="testsheet-form-input"
                      placeholder="Enter total marks"
                      min="0"
                      step="0.5"
                    />
                  </div>

                  {/* Class Section */}
                  <div className="testsheet-form-group" style={{ marginTop: "-1.8rem" }}>
                    <label className="testsheet-form-label">
                      <span className="testsheet-label-icon">🏫</span>
                      Class Section *
                      {apiStatus.sections && (
                        <span
                          className="testsheet-status-badge"
                          style={{
                            backgroundColor: getStatusColor(apiStatus.sections),
                            marginLeft: "8px",
                            fontSize: "0.7em",
                            padding: "2px 6px",
                          }}
                        >
                          {getStatusText(apiStatus.sections)}
                        </span>
                      )}
                    </label>
                    <div className="testsheet-select-wrapper">
                      <select
                        name="classSection"
                        value={formData.classSection}
                        onChange={handleInputChange}
                        className="testsheet-form-select"
                        disabled={
                          apiStatus.sections === "loading" ||
                          apiStatus.sections === "error"
                        }
                      >
                        <option value="">Select Section *</option>
                        {sections.length === 0 ? (
                          <option value="" disabled>
                            {apiStatus.sections === "loading"
                              ? "Loading sections..."
                              : apiStatus.sections === "error"
                              ? "Error loading sections"
                              : "No sections found"}
                          </option>
                        ) : (
                          sections.map((section) => (
                            <option
                              key={section.id}
                              value={section.section_hint}
                            >
                              {section.section_hint}
                            </option>
                          ))
                        )}
                      </select>
                      <div className="testsheet-select-arrow">▼</div>
                    </div>
                  </div>

                  {/* Date */}
                  <div className="testsheet-form-group">
                    <label className="testsheet-form-label">
                      <span className="testsheet-label-icon">
                        <FaCalendarAlt />
                      </span>
                      Date *
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      className="testsheet-form-input"
                    />
                  </div>

                  {/* Day */}
                  <div className="testsheet-form-group">
                    <label className="testsheet-form-label">
                      <span className="testsheet-label-icon">📅</span>
                      Day
                    </label>
                    <div className="testsheet-select-wrapper">
                      <select
                        name="testDay"
                        value={formData.testDay}
                        onChange={handleInputChange}
                        className="testsheet-form-select"
                      >
                        {[
                          "Monday",
                          "Tuesday",
                          "Wednesday",
                          "Thursday",
                          "Friday",
                          "Saturday",
                          "Sunday",
                        ].map((day) => (
                          <option key={day} value={day}>
                            {day}
                          </option>
                        ))}
                      </select>
                      <div className="testsheet-select-arrow">▼</div>
                    </div>
                  </div>
                </div>

                {/* Verification Result Display */}
                {verificationResult && (
                  <div
                    className={`testsheet-verification-result ${
                      verificationResult.isDuplicate ? "testsheet-duplicate" : "testsheet-unique"
                    }`}
                  >
                    <div className="testsheet-verification-icon">
                      {verificationResult.isDuplicate ? (
                        <FaExclamationCircle />
                      ) : (
                        <FaCheckCircle />
                      )}
                    </div>
                    <div className="testsheet-verification-content">
                      <div className="testsheet-verification-title">
                        {verificationResult.isDuplicate
                          ? "Duplicate Detected"
                          : "Details Verified"}
                      </div>
                      <div className="testsheet-verification-message">
                        {verificationResult.message}
                      </div>
                      {verificationResult.generatedTestId && (
                        <div className="testsheet-generated-id">
                          Test ID:{" "}
                          <strong>{verificationResult.generatedTestId}</strong>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Modal Actions - Cancelled aligned left, Others aligned right */}
                <div className="testsheet-modal-actions testsheet-compact-actions">
                  <button
                    type="button"
                    className="testsheet-btn testsheet-btn-outline testsheet-btn-danger-outline"
                    onClick={() => setIsModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <div className="testsheet-action-buttons-right">
                    <button
                      type="button"
                      style={{
                        background: "gold",
                        color: "black",
                        width: "10rem",
                      }}
                      className="testsheet-btn testsheet-btn-info"
                      onClick={verifyTestDetails}
                      disabled={
                        isVerifying ||
                        !formData.testNo ||
                        !formData.testName ||
                        !formData.subject ||
                        !formData.classSection
                      }
                    >
                      {isVerifying ? (
                        <>
                          <FaSpinner className="testsheet-spin" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <FaCheckCircle />
                          Verify
                        </>
                      )}
                    </button>
                    <button
                      type="submit"
                      className="testsheet-btn testsheet-btn-primary"
                      disabled={
                        !formData.testNo ||
                        !formData.testName ||
                        !formData.testId ||
                        !formData.subject ||
                        !formData.classSection ||
                        !formData.totalMarks ||
                        (verificationResult && verificationResult.isDuplicate)
                      }
                    >
                      <FaClipboardCheck />
                      Create Test Sheet
                    </button>
                  </div>
                </div>

                {/* API Status Indicator */}
                <div className="testsheet-api-status-indicator">
                  <div className="testsheet-api-status-item">
                    <span>Subjects:</span>
                    <span
                      className="testsheet-status-dot"
                      style={{
                        backgroundColor: getStatusColor(apiStatus.subjects),
                      }}
                    ></span>
                    <small>{getStatusText(apiStatus.subjects)}</small>
                  </div>
                  <div className="testsheet-api-status-item">
                    <span>Sections:</span>
                    <span
                      className="testsheet-status-dot"
                      style={{
                        backgroundColor: getStatusColor(apiStatus.sections),
                      }}
                    ></span>
                    <small>{getStatusText(apiStatus.sections)}</small>
                  </div>
                </div>

                {/* Help Text */}
                <div className="testsheet-help-text">
                  <p>
                    <small>
                      Fields marked with * are required. Click "Verify Details"
                      to check for duplicates.
                    </small>
                  </p>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="testsheet-wrapper">
          {/* Header */}
          <div className="testsheet-sheet-header">
            <div className="testsheet-header-left">
              <h1 className="testsheet-sheet-title">
                <span className="testsheet-title-icon">📝</span>
                Test Sheet - {className}
              </h1>
              <div className="testsheet-sheet-info">
                <span className="testsheet-info-badge">
                  <FaBook /> {formData.subject || "No Subject"}
                </span>
                <span className="testsheet-info-badge">
                  🏫 {formData.classSection || "No Section"}
                </span>
                {formData.testName && (
                  <span className="testsheet-info-badge testsheet-test-name-badge">
                    📝 {formData.testName}
                  </span>
                )}
                {formData.testId && (
                  <span className="testsheet-info-badge testsheet-test-id-badge">
                    🆔 ID: {formData.testId}
                  </span>
                )}
                <span className="testsheet-info-badge">
                  <FaCalendarAlt /> {formData.date}
                </span>
                <span className="testsheet-info-badge testsheet-keyboard-hint">
                  <FaArrowUp />/<FaArrowDown /> Arrow Keys • Enter = Next
                </span>
              </div>
            </div>

            <div className="testsheet-header-actions">
              {/* NEW Toggle Button for Filters */}
              <button
                className="testsheet-btn testsheet-btn-outline testsheet-toggle-filter-btn"
                onClick={() => setShowFiltersPanel(!showFiltersPanel)}
              >
                {showFiltersPanel ? <><FaTimes /> Hide Filters</> : <><FaFilter /> Show Filters</>}
              </button>
              <button
                className="testsheet-btn testsheet-btn-secondary"
                onClick={() => setIsModalOpen(true)}
              >
                <FaTimes /> Edit Test Details
              </button>
            </div>
          </div>

          {/* Conditionally Rendered Filters & Statistics Panel */}
          {showFiltersPanel && (
            <>
              {/* Statistics */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: "20px",
                  marginTop: "20px",
                }}
              >
                {/* Total Students */}
                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: "12px",
                    padding: "20px",
                    display: "flex",
                    alignItems: "center",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                  }}
                >
                  <div
                    style={{
                      width: "55px",
                      height: "55px",
                      borderRadius: "50%",
                      background: "#e8f5ee",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "26px",
                      marginRight: "15px",
                    }}
                  >
                    👥
                  </div>

                  <div>
                    <div style={{ fontSize: "22px", fontWeight: "700", color: "#017337" }}>
                      {stats.totalStudents}
                    </div>
                    <div style={{ fontSize: "14px", color: "#555" }}>
                      Total Students
                    </div>
                  </div>
                </div>

                {/* Marks Submitted */}
                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: "12px",
                    padding: "20px",
                    display: "flex",
                    alignItems: "center",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                  }}
                >
                  <div
                    style={{
                      width: "55px",
                      height: "55px",
                      borderRadius: "50%",
                      background: "#e3f2fd",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "26px",
                      marginRight: "15px",
                    }}
                  >
                    ✓
                  </div>

                  <div>
                    <div style={{ fontSize: "22px", fontWeight: "700", color: "#0a4983" }}>
                      {stats.submittedStudents}
                    </div>
                    <div style={{ fontSize: "14px", color: "#555" }}>
                      Marks Submitted
                    </div>
                  </div>
                </div>

                {/* Average Marks */}
                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: "12px",
                    padding: "20px",
                    display: "flex",
                    alignItems: "center",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                  }}
                >
                  <div
                    style={{
                      width: "55px",
                      height: "55px",
                      borderRadius: "50%",
                      background: "#fff4e5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "26px",
                      marginRight: "15px",
                    }}
                  >
                    📊
                  </div>

                  <div>
                    <div style={{ fontSize: "22px", fontWeight: "700", color: "#ce2930" }}>
                      {stats.averageMarks}
                    </div>
                    <div style={{ fontSize: "14px", color: "#555" }}>
                      Average Marks
                    </div>
                  </div>
                </div>

                {/* Highest Marks */}
                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: "12px",
                    padding: "20px",
                    display: "flex",
                    alignItems: "center",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                  }}
                >
                  <div
                    style={{
                      width: "55px",
                      height: "55px",
                      borderRadius: "50%",
                      background: "#f3e8ff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "26px",
                      marginRight: "15px",
                    }}
                  >
                    🏆
                  </div>

                  <div>
                    <div style={{ fontSize: "22px", fontWeight: "700", color: "#6a1b9a" }}>
                      {stats.highestMarks}
                    </div>
                    <div style={{ fontSize: "14px", color: "#555" }}>
                      Highest Marks
                    </div>
                  </div>
                </div>
              </div>

              {/* Controls */}
              <div className="testsheet-controls-section" style={{marginTop:'1rem'}}>
                <div className="testsheet-search-box">
                  <FaSearch
                    className="testsheet-search-icon"
                    style={{ marginLeft: "-1rem" }}
                  />
                  <input
                    type="text"
                    placeholder="Search students by name or ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="testsheet-search-input"
                  />
                  {searchTerm && (
                    <button
                      className="testsheet-clear-search"
                      onClick={() => setSearchTerm("")}
                    >
                      ✕
                    </button>
                  )}
                </div>

                <div className="testsheet-bulk-actions">
                  <span className="testsheet-bulk-label">Quick Fill:</span>
                  <input
                    type="number"
                    placeholder="Enter marks"
                    className="testsheet-bulk-input"
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        const marks = parseFloat(e.target.value);
                        if (!isNaN(marks)) {
                          handleBulkMarksUpdate(marks);
                          e.target.value = "";
                          if (inputRefs.current[0]) {
                            inputRefs.current[0].focus();
                            inputRefs.current[0].select();
                          }
                        }
                      }
                    }}
                  />
                  <span className="testsheet-keyboard-shortcut">Enter to apply</span>
                </div>

                <div className="testsheet-action-buttons">
                  <button className="testsheet-btn testsheet-btn-warning" onClick={handleSendData}>
                    <FaPrint /> Print Preview
                  </button>
                  <button
                    className="testsheet-btn testsheet-btn-success"
                    onClick={handleTableSubmit}
                    disabled={
                      isSubmitting || students.length === 0 || !formData.testId
                    }
                  >
                    {isSubmitting ? (
                      <>
                        <FaSpinner className="testsheet-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <FaPaperPlane /> Submit All Marks
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="testsheet-loading-overlay">
              <div className="testsheet-loading-spinner">
                <FaSpinner className="testsheet-spin" size={32} />
                <p>Loading students from PHP backend...</p>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="testsheet-table-container">
            <div className="testsheet-table-wrapper">
              <table className="testsheet-marks-table">
                <thead>
                  <tr>
                    <th className="testsheet-sticky-col">ID</th>
                    <th className="testsheet-sticky-col">Student Name</th>
                    <th>Father's Name</th>
                    <th>Section</th>
                    <th>Total Marks</th>
                    <th className="testsheet-marks-col">Obtained Marks</th>
                    <th>Percentage</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="testsheet-empty-cell">
                        {isLoading ? (
                          <div className="testsheet-loading-inline">
                            <FaSpinner className="testsheet-spin" /> Loading students...
                          </div>
                        ) : (
                          <div className="testsheet-empty-state">
                            <FaExclamationTriangle />
                            <div>
                              <p>No students found for the selected section.</p>
                              <small>
                                {!formData.classSection
                                  ? "Please select a section in the test details."
                                  : "Please check if the section has students registered."}
                              </small>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ) : (
                    filteredStudents.map((student, index) => (
                      <tr
                        key={student.id}
                        className={`${student.status === "submitted" ? "testsheet-submitted" : "testsheet-pending"} ${
                          currentFocusIndex === index ? "testsheet-row-focused" : ""
                        }`}
                        onClick={() => {
                          if (inputRefs.current[index]) {
                            inputRefs.current[index].focus();
                            inputRefs.current[index].select();
                          }
                        }}
                      >
                        <td className="testsheet-sticky-col testsheet-student-id">{student.id}</td>
                        <td className="testsheet-sticky-col testsheet-student-name">
                          {student.name}
                        </td>
                        <td className="testsheet-father-name">{student.father_name}</td>
                        <td>{student.section}</td>
                        <td className="testsheet-total-marks">{student.totalMarks}</td>
                        <td className="testsheet-marks-input-cell">
                          <div className="testsheet-input-wrapper">
                            <input
                              ref={(el) => (inputRefs.current[index] = el)}
                              type="number"
                              value={student.obtainedMarks || ""}
                              onChange={(e) =>
                                handleMarksUpdate(e, student.id, index)
                              }
                              className="testsheet-marks-input"
                              max={student.totalMarks}
                              min="0"
                              placeholder="Enter marks"
                              step="0.5"
                              onFocus={() => setCurrentFocusIndex(index)}
                              onBlur={() => setCurrentFocusIndex(-1)}
                              onKeyDown={(e) => {
                                if (
                                  ["ArrowUp", "ArrowDown", "Enter"].includes(
                                    e.key
                                  )
                                ) {
                                  e.preventDefault();
                                }
                              }}
                            />
                            <div className="testsheet-keyboard-hint-small">↑↓ Enter</div>
                          </div>
                        </td>
                        <td className="testsheet-percentage-cell">
                          <span
                            className={`testsheet-percentage-badge ${
                              student.percentage >= 50 ? "testsheet-good" : "testsheet-poor"
                            }`}
                          >
                            {student.percentage}%
                          </span>
                        </td>
                        <td>
                          <span className={`testsheet-status-badge ${student.status === "submitted" ? "testsheet-submitted" : "testsheet-pending"}`}>
                            {student.status === "submitted"
                              ? "✓ Submitted"
                              : "⏳ Pending"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer Summary */}
          <div className="testsheet-footer-summary">
            <div className="testsheet-summary-content">
              <div className="testsheet-progress-summary">
                <div className="testsheet-progress-bar">
                  <div
                    className="testsheet-progress-fill"
                    style={{ width: `${stats.completionRate}%` }}
                  ></div>
                </div>
                <div className="testsheet-progress-text">
                  {stats.completionRate}% Complete ({stats.submittedStudents}/
                  {stats.totalStudents})
                </div>
                <div className="testsheet-keyboard-tips">
                  <span className="testsheet-tip">
                    <FaArrowUp /> Arrow Up = Previous
                  </span>
                  <span className="testsheet-tip">
                    <FaArrowDown /> Arrow Down = Next
                  </span>
                  <span className="testsheet-tip">Enter = Next Row</span>
                </div>
              </div>
              <div className="testsheet-summary-actions">
                <button
                  className="testsheet-btn testsheet-btn-outline"
                  onClick={() => {
                    if (inputRefs.current[0]) {
                      inputRefs.current[0].focus();
                      inputRefs.current[0].select();
                    }
                  }}
                >
                  Focus First Input
                </button>
                <button
                  className="testsheet-btn testsheet-btn-primary"
                  onClick={handleTableSubmit}
                  disabled={
                    isSubmitting || students.length === 0 || !formData.testId
                  }
                >
                  {isSubmitting ? (
                    <>
                      <FaSpinner className="testsheet-spin" /> Submitting...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane /> Final Submit
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TestSheet;